// app/src/features/report/ReportHost.jsx
import React, { Suspense, lazy } from "react";

const ForecastReportModal = lazy(() => import("./components/ForecastReportModal.jsx"));
const ListenPlayer = lazy(() => import("../../components/listen/ListenPlayer.jsx"));

/**
 * The report modal and the Listen player, rendered beside `AppFrame` (not a frame slot) for the
 * phone and the desktop layouts alike, as the two inline mounts were. Both are fixed-position
 * overlays with their own z-indexes (the modal's backdrop 400 / 460, the player 1000), so their
 * order among the root div's children carries nothing.
 *
 * Both are code-split (`lazy`, the `RichText.jsx` shape): their closures — the report modal with
 * its `lib/report` helpers, and the player — leave the main chunk and load on the first open; the
 * main chunk's cap lives in `app/bundle-budget.json`. A failed chunk load reloads the page once
 * via `lib/chunkReload.js`'s `vite:preloadError` handler, then falls to the app's
 * `ErrorBoundary` (`App.jsx` wraps `AppInner`, which mounts this host).
 *
 * `ctx` is the frame context (`showReport`, the snapshot, the Listen state and `bugOpen` —
 * the player's hotkeys are off while the bug-report dialog is up); `report` is `AppInner`'s
 * `reportCtx`, the modal's own context, passed through untouched.
 */
export default function ReportHost({ ctx, report }) {
  const { showReport, reportSnapshot, reportListen, bugOpen } = ctx;
  // One boundary per overlay, so a pending player chunk never blanks an open modal or the
  // reverse. The fallback is `null`: a click-opened overlay has no stripped-text stand-in (unlike
  // RichText's), so the trade is a click-to-paint gap of one chunk fetch on the first open.
  return (
    <>
      {showReport && reportSnapshot && (
        <Suspense fallback={null}>
          <ForecastReportModal ctx={report} />
        </Suspense>
      )}
      {reportListen.listen && (
        <Suspense fallback={null}>
          <ListenPlayer
            listen={reportListen.listen}
            onClose={reportListen.close}
            onTimelineCue={reportListen.timelineCue}
            keysSuspended={bugOpen}
          />
        </Suspense>
      )}
    </>
  );
}
